import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { decodeUrlParam } from '../../utils/urlUtils';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { useSelector } from 'react-redux';
import GridSectionLoader from '../../components/CustomComponents/preLoad/gridSectionLoader';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import StaffTabBar from './staffTabBar';
import useThemeColor from '../../hooks/useThemeColor';
import useDarkMode from '../../hooks/useDarkMode';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import CustomModal from '../../components/CustomComponents/modal/customModal';
import FormInput from '../../components/CustomComponents/Input/formInput.jsx';
import ModernGridToolbar from '../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar';
import GridEmptyState from '../../components/CustomComponents/ModernGridToolbar/GridEmptyState';
import TableActionsMenu, {
  TableActionsItem,
} from '../../components/CustomComponents/TableActionsMenu/TableActionsMenu';
import moment from 'moment';
import swal from 'sweetalert';
import confirmUnsavedChangesSwal from '../../utils/confirmUnsavedChangesSwal';
import Select from 'react-select';
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from './dropdownStyles.js';
import { normalizeRosterLeaveList } from '../../utils/rosterLeaveUtils';
import './StaffPayout.css';

const PAYOUT_TYPE_OPTIONS = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BI_WEEKLY', label: 'Bi-Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'CUSTOM', label: 'Custom' },
];

const getNumberValue = (record = {}, keys = [], defaultValue = 0) => {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        return num;
      }
    }
  }
  return defaultValue;
};

const normalizeUnpaidHours = (record = {}) => {
  return {
    userName: record.UserName || record.userName || '',
    firstName: record.FirstName || record.firstName || '',
    lastName: record.LastName || record.lastName || '',
    email: record.Email || record.email || '',
    baseRate: getNumberValue(record, ['baseRate', 'BaseRate', 'RatePerHour', 'ratePerHour']),
    totalRegularHours: getNumberValue(record, ['totalRegularHours', 'TotalRegularHours']),
    totalOvertimeHours: getNumberValue(record, ['totalOvertimeHours', 'TotalOvertimeHours']),
    totalBreakHours: getNumberValue(record, ['totalBreakHours', 'TotalBreakHours']),
    unpaidBreakHours: getNumberValue(record, ['unpaidBreakHours', 'UnpaidBreakHours']),
    paidBreakHours: getNumberValue(record, ['paidBreakHours', 'PaidBreakHours']),
    extraBreakHours: getNumberValue(record, ['extraBreakHours', 'ExtraBreakHours']),
    adjustedPaidBreakHours: getNumberValue(record, ['adjustedPaidBreakHours', 'AdjustedPaidBreakHours']),
    totalWorkingHours: getNumberValue(record, ['totalWorkingHours', 'TotalWorkingHours']),
    regularEarnings: getNumberValue(record, ['regularEarnings', 'RegularEarnings']),
    overtimeEarnings: getNumberValue(record, ['overtimeEarnings', 'OvertimeEarnings']),
    paidBreakEarnings: getNumberValue(record, ['paidBreakEarnings', 'PaidBreakEarnings']),
    totalEarnings: getNumberValue(record, ['totalEarnings', 'TotalEarnings']),
    entryCount: getNumberValue(record, ['entryCount', 'EntryCount']),
    firstShiftDate: record.FirstShiftDate || record.firstShiftDate || null,
    lastShiftDate: record.LastShiftDate || record.lastShiftDate || null,
  };
};

const computeAdjustedUnpaidHours = (normalized, staffProfile) => {
  if (!normalized) return null;

  const isSalaryStaff = Boolean(staffProfile?.isSalaryStaff);
  const totalRegularHours = normalized.totalRegularHours || 0;
  const totalOvertimeHours = normalized.totalOvertimeHours || 0;
  const unpaidBreakHours = normalized.unpaidBreakHours || 0;
  const paidBreakHours = normalized.paidBreakHours || 0;
  const extraBreakHours = normalized.extraBreakHours || 0;
  // Use adjusted paid break hours from API (already has extra breaks subtracted)
  const adjustedPaidBreakHours = normalized.adjustedPaidBreakHours ?? (paidBreakHours > 0 && extraBreakHours > 0 ? Math.max(0, paidBreakHours - extraBreakHours) : paidBreakHours);
  const baseRate = staffProfile?.ratePerHour ?? normalized.baseRate ?? 0;
  const overtimeRateFallback = staffProfile?.overTimeHourlyRate || baseRate;

  const regularRate =
    totalRegularHours > 0
      ? (normalized.regularEarnings || 0) / totalRegularHours
      : baseRate;

  const overtimeRate =
    totalOvertimeHours > 0
      ? (normalized.overtimeEarnings || 0) / totalOvertimeHours
      : overtimeRateFallback || regularRate;

  let adjustedRegularHours = totalRegularHours;
  let adjustedOvertimeHours = totalOvertimeHours;
  let remainingDeduction = isSalaryStaff ? 0 : unpaidBreakHours;

  // For hourly staff: deduct extra break hours from payable hours
  if (!isSalaryStaff && extraBreakHours > 0) {
    remainingDeduction += extraBreakHours;
  }

  if (remainingDeduction > 0 && adjustedRegularHours > 0) {
    const deduction = Math.min(remainingDeduction, adjustedRegularHours);
    adjustedRegularHours -= deduction;
    remainingDeduction -= deduction;
  }

  if (remainingDeduction > 0 && adjustedOvertimeHours > 0) {
    const deduction = Math.min(remainingDeduction, adjustedOvertimeHours);
    adjustedOvertimeHours -= deduction;
    remainingDeduction -= deduction;
  }

  // For earnings: add adjusted paid break hours to regular hours
  // Use paid break earnings from API (already calculated with adjusted hours) or calculate it
  const adjustedPaidBreakEarnings = normalized.paidBreakEarnings || (adjustedPaidBreakHours * regularRate);
  const payableRegularHoursWithBreaks = adjustedRegularHours + adjustedPaidBreakHours;
  const payableRegularEarnings = payableRegularHoursWithBreaks * regularRate;
  const payableOvertimeEarnings = adjustedOvertimeHours * overtimeRate;
  const payableWorkingEarnings = payableRegularEarnings + payableOvertimeEarnings;
  // Total earnings includes regular (with paid breaks) + overtime
  const payableTotalEarnings = payableWorkingEarnings;

  return {
    ...normalized,
    regularRate,
    overtimeRate,
    payableRegularHours: adjustedRegularHours,
    payableOvertimeHours: adjustedOvertimeHours,
    payableHours: adjustedRegularHours + adjustedOvertimeHours,
    payableRegularEarnings,
    payableOvertimeEarnings,
    payableWorkingEarnings,
    payableTotalEarnings,
    unpaidBreakAdjustmentHours: isSalaryStaff ? 0 : unpaidBreakHours,
    adjustedPaidBreakHours,
    adjustedPaidBreakEarnings,
    extraBreakHours,
  };
};

const payoutFormSnapshot = (form, leavesSet) => ({
  ...form,
  startDate: form.startDate ? moment(form.startDate).format('YYYY-MM-DD') : null,
  endDate: form.endDate ? moment(form.endDate).format('YYYY-MM-DD') : null,
  bonusAmount: Number(form.bonusAmount) || 0,
  deductionAmount: Number(form.deductionAmount) || 0,
  taxAmount: Number(form.taxAmount) || 0,
  selectedLeaveIds: [...(leavesSet || [])].sort((a, b) => a - b),
});

const StaffPayout = () => {
  const { UserName: encodedUserName } = useParams();
  const UserName = decodeUrlParam(encodedUserName);
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [search, setSearch] = useState('');
  const [rawUnpaidHours, setRawUnpaidHours] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf('month').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('month').toDate());
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [selectedPayoutEntries, setSelectedPayoutEntries] = useState([]);
  const [selectedPayoutLeaves, setSelectedPayoutLeaves] = useState([]);
  const [isPayoutDetailsLoading, setIsPayoutDetailsLoading] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingPayoutID, setProcessingPayoutID] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: '',
    paymentReference: '',
    paymentNotes: ''
  });
  const [payoutForm, setPayoutForm] = useState({
    startDate: moment().startOf('week').toDate(),
    endDate: moment().endOf('week').toDate(),
    payoutType: 'WEEKLY',
    bonusAmount: 0,
    deductionAmount: 0,
    taxAmount: 0,
    notes: ''
  });
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [selectedLeaves, setSelectedLeaves] = useState(new Set()); // For salary staff to include/exclude leaves
  const generateModalInitialSnapshot = useRef(null);

  const unpaidHours = useMemo(
    () => computeAdjustedUnpaidHours(rawUnpaidHours, staffProfile),
    [rawUnpaidHours, staffProfile]
  );

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === "Rs") return "PKR";
    return currencyCode || "GBP";
  };

  useEffect(() => {
    if (profileData?.compCurrency) {
      const normalizedCurrency = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalizedCurrency);
    }
  }, [profileData]);

  const formatCurrency = (amount) => {
    try {
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };

  const fetchPayouts = useCallback(async () => {
    if (!CompID || !UserName) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_PAYOUTS, {
        params: {
          compID: CompID,
          userName: UserName,
          pageNumber: 1,
          pageSize: 100
        }
      });

      const data = response.data?.data || response.data || [];
      setPayouts(data);
    } catch (err) {
      showErrorToast('Failed to fetch payouts');
      console.error('Error fetching payouts:', err);
    } finally {
      setLoading(false);
    }
  }, [CompID, UserName]);

  const fetchStaffProfile = useCallback(async () => {
    if (!CompID || !UserName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: {
          CompID: CompID,
          UserName: UserName
        }
      });

      const data = response.data?.data || response.data;
      if (data) {
        // If it's an array, take the first element, otherwise use the data directly
        setStaffProfile(Array.isArray(data) ? data[0] : data);
      }
    } catch (err) {
      console.error('Error fetching staff profile:', err);
    }
  }, [CompID, UserName]);

  const fetchUnpaidHours = useCallback(async () => {
    if (!CompID || !UserName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_UNPAID_HOURS, {
        params: {
          compID: CompID,
          userName: UserName,
          startDate: payoutForm.startDate ? moment(payoutForm.startDate).format('YYYY-MM-DD') : null,
          endDate: payoutForm.endDate ? moment(payoutForm.endDate).format('YYYY-MM-DD') : null
        }
      });

      const data = response.data?.data || response.data || [];
      if (data.length > 0) {
        const unpaidData = data[0];
        const normalized = normalizeUnpaidHours(unpaidData);
        setRawUnpaidHours(normalized);
      } else {
        setRawUnpaidHours(null);
      }
    } catch (err) {
      console.error('Error fetching unpaid hours:', err);
      setRawUnpaidHours(null);
    }
  }, [CompID, UserName, payoutForm.startDate, payoutForm.endDate]);

  useEffect(() => {
    fetchPayouts();
    fetchStaffProfile();
  }, [fetchPayouts, fetchStaffProfile]);

  const fetchStaffLeaves = useCallback(async () => {
    if (!CompID || !UserName) return;
    
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_ROSTER_LEAVE_DAYS, {
        params: {
          CompID,
          UserName,
          StartDate: payoutForm.startDate ? moment(payoutForm.startDate).format('YYYY-MM-DD') : null,
          EndDate: payoutForm.endDate ? moment(payoutForm.endDate).format('YYYY-MM-DD') : null,
        },
      });

      const data = response.data?.data || response.data || [];
      const normalizedLeaves = normalizeRosterLeaveList(data).filter(
        (leave) => !leave.Payout_ID
      );

      setStaffLeaves(normalizedLeaves);
      // For hourly staff, auto-select all paid leaves; for salary staff, start with none selected
      // Wait for staffProfile to be loaded before setting selection
      if (staffProfile) {
        if (staffProfile.isSalaryStaff) {
          setSelectedLeaves(new Set());
        } else {
          // Auto-select paid leaves for hourly staff
          const paidLeaveIds = new Set(
            normalizedLeaves.filter(l => l.IsPaid).map(l => l.SR_ID || l.SL_ID)
          );
          setSelectedLeaves(paidLeaveIds);
        }
      }
    } catch (error) {
      console.error('Error fetching staff leaves:', error);
      setStaffLeaves([]);
    }
  }, [CompID, UserName, payoutForm.startDate, payoutForm.endDate, staffProfile?.isSalaryStaff]);

  useEffect(() => {
    if (showGenerateModal) {
      generateModalInitialSnapshot.current = JSON.stringify(
        payoutFormSnapshot(payoutForm, selectedLeaves)
      );
    } else {
      generateModalInitialSnapshot.current = null;
    }
  }, [showGenerateModal]);

  const handleGeneratePayoutInternal = useCallback(async (exitAfterSave = false) => {
    if (!unpaidHours || unpaidHours.payableHours <= 0) {
      showErrorToast('No unpaid hours found for the selected period');
      return false;
    }

    let leaveAdjustment = 0;
    let includedLeaveIds = [];

    if (staffProfile?.isSalaryStaff) {
      const selectedLeavesList = staffLeaves.filter(l => selectedLeaves.has(l.SR_ID || l.SL_ID));
      const daysInPeriod = moment(payoutForm.endDate).diff(moment(payoutForm.startDate), 'days') + 1;
      const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);

      selectedLeavesList.forEach(leave => {
        const leaveDays = 1;
        const proRatedAmount = dailySalary * leaveDays;
        if (!leave.IsPaid) {
          leaveAdjustment -= proRatedAmount;
        }
        includedLeaveIds.push(leave.SR_ID || leave.SL_ID);
      });
    } else {
      const paidLeaves = staffLeaves.filter(l => l.IsPaid);
      const hourlyRate = staffProfile?.ratePerHour || 0;
      paidLeaves.forEach(leave => {
        leaveAdjustment += (leave.TotalHours || 0) * hourlyRate;
        includedLeaveIds.push(leave.SR_ID || leave.SL_ID);
      });
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.GENERATE_PAYOUT, {
        compID: CompID,
        userName: UserName,
        startDate: moment(payoutForm.startDate).format('YYYY-MM-DD'),
        endDate: moment(payoutForm.endDate).format('YYYY-MM-DD'),
        payoutType: payoutForm.payoutType,
        bonusAmount: parseFloat(payoutForm.bonusAmount) || 0,
        deductionAmount: parseFloat(payoutForm.deductionAmount) || 0,
        taxAmount: parseFloat(payoutForm.taxAmount) || 0,
        notes: payoutForm.notes,
        createdBy: UserName,
        includedLeaveIds: includedLeaveIds.length > 0 ? includedLeaveIds.join(',') : null,
        leaveAdjustmentAmount: leaveAdjustment
      });

      if (response.data.status) {
        showSuccessToast('Payout generated successfully!');
        setShowGenerateModal(false);
        fetchPayouts();
        setPayoutForm({
          startDate: moment().startOf('week').toDate(),
          endDate: moment().endOf('week').toDate(),
          payoutType: 'WEEKLY',
          bonusAmount: 0,
          deductionAmount: 0,
          taxAmount: 0,
          notes: ''
        });
        setStaffLeaves([]);
        setSelectedLeaves(new Set());
        generateModalInitialSnapshot.current = null;
        if (exitAfterSave) {
          navigate('/staff/manageStaff');
        }
        return true;
      }
      showErrorToast(response.data.message || 'Failed to generate payout');
      return false;
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error generating payout');
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    unpaidHours, staffProfile, staffLeaves, selectedLeaves, payoutForm,
    CompID, UserName, navigate
  ]);

  const handleGeneratePayout = (exitAfterSave = false) => () => {
    handleGeneratePayoutInternal(exitAfterSave);
  };

  const isGenerateModalDirty = useMemo(() => {
    if (!showGenerateModal || !generateModalInitialSnapshot.current) return false;
    const current = JSON.stringify(payoutFormSnapshot(payoutForm, selectedLeaves));
    return current !== generateModalInitialSnapshot.current;
  }, [showGenerateModal, payoutForm, selectedLeaves]);

  const handleTabChangeBeforeNavigate = useCallback(
    async (clickedTabId, clickedTabLink) => {
      if (!showGenerateModal || !isGenerateModalDirty) return true;

      const result = await confirmUnsavedChangesSwal({
        text: 'You have unsaved changes in the Generate Payout form. Do you want to save before leaving?',
      });

      if (!result) return false;
      if (result === 'discard') {
        setShowGenerateModal(false);
        return true;
      }
      if (result === 'save') {
        const success = await handleGeneratePayoutInternal(false);
        if (success && clickedTabLink) navigate(clickedTabLink);
        return false;
      }
      return false;
    },
    [showGenerateModal, isGenerateModalDirty, navigate, handleGeneratePayoutInternal]
  );

  useEffect(() => {
    if (showGenerateModal) {
      // Ensure staff profile is loaded
      if (!staffProfile) {
        fetchStaffProfile();
      }
      fetchUnpaidHours();
      // Only fetch leaves if staffProfile is available or will be available
      if (staffProfile) {
        fetchStaffLeaves();
      }
    }
  }, [showGenerateModal, staffProfile, fetchUnpaidHours, fetchStaffLeaves, fetchStaffProfile]);

  // Fetch leaves when staffProfile becomes available
  useEffect(() => {
    if (showGenerateModal && staffProfile && staffLeaves.length === 0) {
      fetchStaffLeaves();
    }
  }, [showGenerateModal, staffProfile, fetchStaffLeaves]);

  const handleViewPayoutDetails = async (payout) => {
    if (!CompID || !payout) {
      if (!CompID) {
        showErrorToast('Company information not found');
      }
      return;
    }

    setSelectedPayout(payout);
    setSelectedPayoutEntries([]);
    setSelectedPayoutLeaves([]);
    setIsPayoutDetailsLoading(true);

    try {
      const response = await axiosInstance.get(APIs.GET_PAYOUT_DETAILS, {
        params: {
          payoutID: payout.payout_ID || payout.Payout_ID,
          compID: CompID
        }
      });

      const payload = response.data?.data || response.data || {};
      const payoutDetails = payload.Payout || payload.payout || payout;
      const entriesData = payload.Entries || payload.entries || [];
      const leavesData = payload.Leaves || payload.leaves || [];

      setSelectedPayout(payoutDetails);
      setSelectedPayoutEntries(entriesData);
      setSelectedPayoutLeaves(leavesData);
    } catch (error) {
      console.error('Error fetching payout details:', error);
      showErrorToast('Failed to load payout details');
    } finally {
      setIsPayoutDetailsLoading(false);
    }
  };

  const handleClosePayoutDetails = () => {
    setSelectedPayout(null);
    setSelectedPayoutEntries([]);
    setSelectedPayoutLeaves([]);
    setIsPayoutDetailsLoading(false);
  };

  const handleProcessPayout = (payoutID) => {
    setProcessingPayoutID(payoutID);
    setPaymentForm({
      paymentMethod: '',
      paymentReference: '',
      paymentNotes: ''
    });
    setShowProcessModal(true);
  };

  const confirmProcessPayout = async () => {
    if (!paymentForm.paymentMethod) {
      showErrorToast('Please select a payment method');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.PROCESS_PAYOUT, {
        payoutID: processingPayoutID,
        compID: CompID,
        paymentMethod: paymentForm.paymentMethod,
        paymentReference: paymentForm.paymentReference || '',
        paymentNotes: paymentForm.paymentNotes || '',
        paidBy: UserName
      });

      if (response.data.status) {
        showSuccessToast('Payment processed successfully!');
        setShowProcessModal(false);
        fetchPayouts();
      } else {
        showErrorToast(response.data.message || 'Failed to process payment');
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error processing payment');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-warning';
      case 'APPROVED': return 'bg-info';
      case 'PAID': return 'bg-success';
      case 'CANCELLED': return 'bg-secondary';
      case 'REJECTED': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const filteredPayouts = useMemo(() => {
    if (!search.trim()) return payouts;
    const q = search.trim().toLowerCase();
    return payouts.filter((item) => {
      const payout = item.payout || item;
      const period = `${moment(payout.period_StartDate || payout.Period_StartDate).format('MMM DD YYYY')} ${moment(payout.period_EndDate || payout.Period_EndDate).format('MMM DD YYYY')}`;
      const type = String(payout.payout_Type || payout.Payout_Type || '');
      const status = String(payout.payout_Status || payout.Payout_Status || '');
      const notes = String(payout.notes || payout.Notes || '');
      return (
        period.toLowerCase().includes(q) ||
        type.toLowerCase().includes(q) ||
        status.toLowerCase().includes(q) ||
        notes.toLowerCase().includes(q)
      );
    });
  }, [payouts, search]);

  const handleCloseGenerateModal = useCallback(async () => {
    if (!isGenerateModalDirty) {
      setShowGenerateModal(false);
      return;
    }
    const result = await swal({
      title: 'Unsaved changes',
      text: 'You have unsaved changes in the Generate Payout form. Discard them?',
      icon: 'warning',
      buttons: {
        cancel: 'Keep editing',
        discard: { text: 'Discard', value: 'discard', className: 'swal-button--danger' },
      },
      dangerMode: true,
    });
    if (result === 'discard') {
      setShowGenerateModal(false);
    }
  }, [isGenerateModalDirty]);

  const handlePayPeriodChange = (update) => {
    const [newStart, newEnd] = update;
    if (!newStart && !newEnd) {
      setPayoutForm((prev) => ({
        ...prev,
        startDate: moment().startOf('week').toDate(),
        endDate: moment().endOf('week').toDate(),
      }));
      setSelectedLeaves(new Set());
      return;
    }
    if (
      (!newStart || moment(newStart).isValid()) &&
      (!newEnd || moment(newEnd).isValid())
    ) {
      setPayoutForm((prev) => ({
        ...prev,
        startDate: newStart,
        endDate: newEnd || newStart,
      }));
      setSelectedLeaves(new Set());
    }
  };

  const dropdownStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );

  const selectedPayoutType =
    PAYOUT_TYPE_OPTIONS.find((o) => o.value === payoutForm.payoutType) ||
    PAYOUT_TYPE_OPTIONS[0];

  return (
    <div className={`staff-payout-page ${isDarkMode ? 'staff-payout-page--dark' : ''}`}>
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/staff/manageStaff">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Staff Payouts</li>
          </ol>
          <h1 className="page-header mb-0">
            Staff Payouts – {([staffProfile?.firstName ?? staffProfile?.FirstName, staffProfile?.lastName ?? staffProfile?.LastName].filter(Boolean).join(" ").trim()) || UserName} ({UserName})
          </h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            onClick={() => setShowGenerateModal(true)}
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Generate Payout
          </button>
        </div>
      </div>

      <div className="card border-0">
        <StaffTabBar
          activeTab="payout"
          useTab="false"
          UserName={UserName}
          onBeforeTabChange={handleTabChangeBeforeNavigate}
        />

        <div className="mb-3" style={{ position: 'relative', zIndex: 10 }}>
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Payouts"
            totalCount={filteredPayouts.length}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search payouts..."
            bodyFlush
          >
            <GridSectionLoader loading={loading} minHeight="280px">
              {filteredPayouts.length === 0 ? (
                <GridEmptyState
                  isDarkMode={isDarkMode}
                  themeColor={themeColor}
                  title="No payouts found"
                  description={
                    search.trim()
                      ? 'No payouts match your search. Try a different keyword.'
                      : 'Generate a payout to get started for this staff member.'
                  }
                  onClearFilters={search.trim() ? () => setSearch('') : undefined}
                  clearFiltersLabel="Clear search"
                />
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead
                      className="bg-light"
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                      }}
                    >
                      <tr>
                        <th className="pt-3 pb-3">Period</th>
                        <th className="pt-3 pb-3 text-center">Type</th>
                        <th className="pt-3 pb-3 text-center">Staff Type</th>
                        <th className="pt-3 pb-3 text-center">Working Hours</th>
                        <th className="pt-3 pb-3 text-center">Regular</th>
                        <th className="pt-3 pb-3 text-center">Overtime</th>
                        <th className="pt-3 pb-3 text-end">Net Amount</th>
                        <th className="pt-3 pb-3 text-center">Status</th>
                        <th className="pt-3 pb-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayouts.map((item) => {
                        const payout = item.payout || item;
                        const status = payout.payout_Status || payout.Payout_Status;
                        const payoutId = payout.payout_ID || payout.Payout_ID;
                        return (
                          <tr key={payoutId}>
                            <td className="align-middle">
                              <div className="fw-semibold">
                                {moment(payout.period_StartDate || payout.Period_StartDate).format('MMM DD')} –{' '}
                                {moment(payout.period_EndDate || payout.Period_EndDate).format('MMM DD, YYYY')}
                              </div>
                              <div className="text-muted small">
                                Created: {moment(payout.createdDateTime || payout.CreatedDateTime).format('MMM DD, YYYY')}
                              </div>
                            </td>
                            <td className="align-middle text-center">
                              <span className="badge bg-secondary">
                                {payout.payout_Type || payout.Payout_Type}
                              </span>
                            </td>
                            <td className="align-middle text-center">
                              {(payout.staff_Type || payout.Staff_Type) ? (
                                <div>
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor:
                                        (payout.staff_Type || payout.Staff_Type) === 'HOURLY'
                                          ? '#17a2b8'
                                          : (payout.staff_Type || payout.Staff_Type) === 'WEEKLY_SALARY'
                                            ? '#28a745'
                                            : '#6f42c1',
                                      color: 'white',
                                    }}
                                  >
                                    {(payout.staff_Type || payout.Staff_Type) === 'HOURLY'
                                      ? 'Hourly'
                                      : (payout.staff_Type || payout.Staff_Type) === 'WEEKLY_SALARY'
                                        ? 'Weekly Salary'
                                        : 'Monthly Salary'}
                                  </span>
                                  {(payout.calculation_Method || payout.Calculation_Method) && (
                                    <div className="text-muted small mt-1">
                                      {payout.calculation_Method || payout.Calculation_Method}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="align-middle text-center">
                              <span className="fw-semibold">
                                {(payout.total_Working_Hours || payout.Total_Working_Hours || 0).toFixed(1)} hrs
                              </span>
                            </td>
                            <td className="align-middle text-center">
                              {(payout.total_Regular_Hours || payout.Total_Regular_Hours || 0).toFixed(1)} hrs
                            </td>
                            <td className="align-middle text-center">
                              {(payout.total_Overtime_Hours || payout.Total_Overtime_Hours || 0).toFixed(1)} hrs
                            </td>
                            <td className="align-middle text-end">
                              <div className="fw-bold fs-6">
                                {formatCurrency(payout.net_Amount || payout.Net_Amount || 0)}
                              </div>
                              {(payout.gross_Amount || payout.Gross_Amount) !==
                                (payout.net_Amount || payout.Net_Amount) && (
                                <div className="text-muted small">
                                  Gross: {formatCurrency(payout.gross_Amount || payout.Gross_Amount || 0)}
                                </div>
                              )}
                            </td>
                            <td className="align-middle text-center">
                              <span className={`badge ${getStatusBadgeClass(status)}`}>
                                {status}
                              </span>
                            </td>
                            <td
                              className="align-middle text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TableActionsMenu dropup menuEnd>
                                <TableActionsItem
                                  icon="fa fa-eye"
                                  onClick={() => handleViewPayoutDetails(payout)}
                                >
                                  View Details
                                </TableActionsItem>
                                {status === 'APPROVED' && (
                                  <TableActionsItem
                                    icon="fa fa-money-bill"
                                    onClick={() => handleProcessPayout(payoutId)}
                                  >
                                    Process Payment
                                  </TableActionsItem>
                                )}
                              </TableActionsMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </GridSectionLoader>
          </ModernGridToolbar>
        </div>
      </div>

      {/* Generate Payout Modal */}
      <CustomModal
        isOpen={showGenerateModal}
        onClose={handleCloseGenerateModal}
        title="Generate Payout"
        subtitle="Review unpaid hours, set the pay period, then save"
        isCentered
        modalSize="modal-lg"
        maxHeight="88vh"
        showButton={false}
      >
        <div className={`staff-payout-modal ${isDarkMode ? 'staff-payout-modal--dark' : ''}`}>
          <div className="staff-payout-modal__field staff-payout-modal__field--top">
            <label className="form-label">Pay Period</label>
            <div className="staff-payout-modal__period">
              <DateRangePicker
                startDate={
                  payoutForm.startDate && moment(payoutForm.startDate).isValid()
                    ? payoutForm.startDate
                    : null
                }
                endDate={
                  payoutForm.endDate && moment(payoutForm.endDate).isValid()
                    ? payoutForm.endDate
                    : null
                }
                onChange={handlePayPeriodChange}
                placeholder="Select pay period"
                allowSingleDate={false}
                panelMaxWidth={500}
              />
            </div>
          </div>

          {unpaidHours && (
            <div className="staff-payout-modal__summary">
              <div
                className="staff-payout-modal__stat staff-payout-modal__stat--accent"
                style={{ '--spm-accent': themeColor }}
              >
                <div className="staff-payout-modal__stat-icon">
                  <i className="fa fa-clock"></i>
                </div>
                <div className="staff-payout-modal__stat-body">
                  <span className="staff-payout-modal__stat-label">Payable Hours</span>
                  <span className="staff-payout-modal__stat-value">
                    {unpaidHours.payableHours.toFixed(1)} hrs
                  </span>
                  <span className="staff-payout-modal__stat-meta">
                    Recorded {unpaidHours.totalWorkingHours.toFixed(1)} hrs
                    {(unpaidHours.unpaidBreakAdjustmentHours > 0 || (unpaidHours.extraBreakHours || 0) > 0) && (
                      <>
                        {' · '}
                        <span className="text-danger">
                          -
                          {(
                            (unpaidHours.unpaidBreakAdjustmentHours || 0) +
                            (unpaidHours.extraBreakHours || 0)
                          ).toFixed(1)}{' '}
                          breaks
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>

              <div className="staff-payout-modal__stat">
                <div className="staff-payout-modal__stat-icon staff-payout-modal__stat-icon--warn">
                  <i className="fa fa-coffee"></i>
                </div>
                <div className="staff-payout-modal__stat-body">
                  <span className="staff-payout-modal__stat-label">Break Hours</span>
                  <span className="staff-payout-modal__stat-value">
                    {(unpaidHours.totalBreakHours || 0).toFixed(1)} hrs
                  </span>
                  <span className="staff-payout-modal__stat-meta">
                    {(unpaidHours.adjustedPaidBreakHours || 0) > 0 && (
                      <span className="text-success">
                        Paid {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)}
                      </span>
                    )}
                    {(unpaidHours.unpaidBreakHours || 0) > 0 && (
                      <span>
                        {(unpaidHours.adjustedPaidBreakHours || 0) > 0 ? ' · ' : ''}
                        Unpaid {(unpaidHours.unpaidBreakHours || 0).toFixed(1)}
                      </span>
                    )}
                    {(unpaidHours.adjustedPaidBreakHours || 0) <= 0 &&
                      (unpaidHours.unpaidBreakHours || 0) <= 0 &&
                      'No breaks recorded'}
                  </span>
                </div>
              </div>

              <div
                className="staff-payout-modal__stat staff-payout-modal__stat--accent"
                style={{ '--spm-accent': themeColor }}
              >
                <div className="staff-payout-modal__stat-icon">
                  <i className="fa fa-money-bill-wave"></i>
                </div>
                <div className="staff-payout-modal__stat-body">
                  <span className="staff-payout-modal__stat-label">Earnings</span>
                  <span className="staff-payout-modal__stat-value">
                    {formatCurrency(unpaidHours.payableTotalEarnings || 0)}
                  </span>
                  <span className="staff-payout-modal__stat-meta">
                    Reg {formatCurrency(unpaidHours.payableRegularEarnings || 0)} · OT{' '}
                    {formatCurrency(unpaidHours.payableOvertimeEarnings || 0)}
                  </span>
                </div>
              </div>

              <div className="staff-payout-modal__stat">
                <div className="staff-payout-modal__stat-icon staff-payout-modal__stat-icon--split">
                  <i className="fa fa-chart-pie"></i>
                </div>
                <div className="staff-payout-modal__stat-body">
                  <span className="staff-payout-modal__stat-label">Split</span>
                  <span className="staff-payout-modal__stat-value">
                    {unpaidHours.payableRegularHours.toFixed(1)} / {unpaidHours.payableOvertimeHours.toFixed(1)}
                  </span>
                  <span className="staff-payout-modal__stat-meta">Regular / Overtime hrs</span>
                </div>
              </div>
            </div>
          )}

          {staffLeaves.length > 0 && (
            staffProfile ? (
              <div className={`staff-payout-modal__leaves ${staffProfile.isSalaryStaff ? 'is-salary' : 'is-hourly'}`}>
                <div className="staff-payout-modal__leaves-head">
                  <div className="staff-payout-modal__leaves-title">
                    <i className="fa fa-calendar-times"></i>
                    <span>Leave Records</span>
                    <span className="badge bg-secondary">{staffLeaves.length}</span>
                  </div>
                </div>
                {staffProfile.isSalaryStaff ? (
                  <>
                    <p className="staff-payout-modal__leaves-hint">
                      Select leaves to include. Paid leaves are already in base salary; unpaid leaves are deducted.
                    </p>
                    <div className="staff-payout-modal__leave-list">
                      {staffLeaves.map((leave) => {
                        const leaveId = leave.SR_ID || leave.SL_ID;
                        const isSelected = selectedLeaves.has(leaveId);
                        const leaveDays = 1;
                        const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                        const proRatedAmount = dailySalary * leaveDays;

                        return (
                          <label key={leaveId} className={`staff-payout-modal__leave-row ${isSelected ? 'is-selected' : ''}`}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              onChange={(e) => {
                                const next = new Set(selectedLeaves);
                                if (e.target.checked) next.add(leaveId);
                                else next.delete(leaveId);
                                setSelectedLeaves(next);
                              }}
                            />
                            <div className="staff-payout-modal__leave-main">
                              <div className="staff-payout-modal__leave-top">
                                <strong>{leave.LeaveType || 'Leave'}</strong>
                                <span className={`badge ${leave.IsPaid ? 'bg-success' : 'bg-secondary'}`}>
                                  {leave.IsPaid ? 'Paid' : 'Unpaid'}
                                </span>
                              </div>
                              <div className="staff-payout-modal__leave-sub">
                                {moment(leave.StartDate).format('MMM DD, YYYY')} ({leaveDays} day)
                                {leave.Reason ? ` · ${leave.Reason}` : ''}
                              </div>
                            </div>
                            <div className="staff-payout-modal__leave-amount">
                              {isSelected
                                ? leave.IsPaid
                                  ? formatCurrency(0)
                                  : `-${formatCurrency(proRatedAmount)}`
                                : formatCurrency(proRatedAmount)}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div className="staff-payout-modal__leaves-total">
                      <span>Total leave adjustment</span>
                      <strong>
                        {(() => {
                          const selectedLeavesList = staffLeaves.filter((l) =>
                            selectedLeaves.has(l.SR_ID || l.SL_ID)
                          );
                          const dailySalary =
                            (staffProfile.salary || 0) /
                            (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                          const total = selectedLeavesList.reduce((sum, leave) => {
                            return sum + (leave.IsPaid ? 0 : -dailySalary);
                          }, 0);
                          return (
                            formatCurrency(Math.abs(total)) +
                            (total < 0 ? ' (deduction)' : '')
                          );
                        })()}
                      </strong>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="staff-payout-modal__leaves-hint">
                      Paid leave hours are included automatically in this payout.
                    </p>
                    <div className="staff-payout-modal__leave-list">
                      {staffLeaves
                        .filter((l) => l.IsPaid)
                        .map((leave) => {
                          const hourlyRate = staffProfile.ratePerHour || 0;
                          const leaveEarnings = (leave.TotalHours || 0) * hourlyRate;
                          return (
                            <div key={leave.SR_ID || leave.SL_ID} className="staff-payout-modal__leave-row is-readonly">
                              <div className="staff-payout-modal__leave-main">
                                <div className="staff-payout-modal__leave-top">
                                  <strong>{leave.LeaveType || 'Leave'}</strong>
                                  <span className="badge bg-success">Paid</span>
                                </div>
                                <div className="staff-payout-modal__leave-sub">
                                  {moment(leave.StartDate).format('MMM DD, YYYY')} ·{' '}
                                  {(leave.TotalHours || 0).toFixed(1)} hrs
                                </div>
                              </div>
                              <div className="staff-payout-modal__leave-amount is-credit">
                                +{formatCurrency(leaveEarnings)}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    <div className="staff-payout-modal__leaves-total">
                      <span>Total leave earnings</span>
                      <strong className="text-success">
                        {(() => {
                          const paidLeaves = staffLeaves.filter((l) => l.IsPaid);
                          const hourlyRate = staffProfile.ratePerHour || 0;
                          const total = paidLeaves.reduce(
                            (sum, leave) => sum + (leave.TotalHours || 0) * hourlyRate,
                            0
                          );
                          return formatCurrency(total);
                        })()}
                      </strong>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="staff-payout-modal__loading">
                <i className="fa fa-spinner fa-spin me-2"></i>
                Loading staff information...
              </div>
            )
          )}

          <div className="staff-payout-modal__form">
            <div className="staff-payout-modal__section-label">Pay details</div>

            <div className="row g-3">
              <div className="col-md-6">
                <label
                  className="form-label"
                  style={{ color: isDarkMode ? '#ffffff' : '#000000' }}
                >
                  Payout Type
                </label>
                <Select
                  value={selectedPayoutType}
                  onChange={(option) =>
                    setPayoutForm({
                      ...payoutForm,
                      payoutType: option?.value || 'WEEKLY',
                    })
                  }
                  options={PAYOUT_TYPE_OPTIONS}
                  styles={dropdownStyles}
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Bonus Amount"
                  placeHolder="0.00"
                  type="number"
                  step="0.01"
                  name="bonusAmount"
                  value={payoutForm.bonusAmount}
                  onchange={(e) =>
                    setPayoutForm({ ...payoutForm, bonusAmount: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Deduction Amount"
                  placeHolder="0.00"
                  type="number"
                  step="0.01"
                  name="deductionAmount"
                  value={payoutForm.deductionAmount}
                  onchange={(e) =>
                    setPayoutForm({ ...payoutForm, deductionAmount: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Tax Amount"
                  placeHolder="0.00"
                  type="number"
                  step="0.01"
                  name="taxAmount"
                  value={payoutForm.taxAmount}
                  onchange={(e) =>
                    setPayoutForm({ ...payoutForm, taxAmount: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <FormInput
                  label="Notes"
                  placeHolder="Optional notes for this payout..."
                  type="text"
                  name="notes"
                  value={payoutForm.notes}
                  onchange={(e) =>
                    setPayoutForm({ ...payoutForm, notes: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <div className="staff-payout-modal__footer">
            <button
              type="button"
              className="btn staff-payout-modal__btn-cancel"
              onClick={handleCloseGenerateModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn staff-payout-modal__btn-primary"
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                borderColor: themeColor,
              }}
              onClick={handleGeneratePayout(false)}
              disabled={!unpaidHours || unpaidHours.payableHours <= 0 || loading}
            >
              <i className="fa fa-save me-2"></i>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </CustomModal>

      {/* Payout Details Modal */}
      {selectedPayout && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content" style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000'
            }}>
              <div className="modal-header" style={{
                borderColor: isDarkMode ? '#404040' : '#dee2e6',
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000'
              }}>
                <h5 className="modal-title">Payout Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClosePayoutDetails}
                  style={{ filter: isDarkMode ? 'invert(1)' : 'none' }}
                ></button>
              </div>
              <div className="modal-body">
                {isPayoutDetailsLoading && (
                  <div className="alert alert-info d-flex align-items-center">
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Loading payout details...
                  </div>
                )}
                {/* Payout Summary */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <div className="card border-0 bg-primary text-white">
                      <div className="card-body p-3">
                        <div className="small">Working Hours</div>
                        <div className="fs-4 fw-bold">{(selectedPayout.total_Working_Hours || selectedPayout.Total_Working_Hours || 0).toFixed(1)}</div>
                        {selectedPayoutEntries.length > 0 && (
                          <div className="text-white-50 small">
                            {selectedPayoutEntries.length} entr{selectedPayoutEntries.length === 1 ? 'y' : 'ies'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-success text-white">
                      <div className="card-body p-3">
                        <div className="small">Regular Hours</div>
                        <div className="fs-4 fw-bold">{(selectedPayout.total_Regular_Hours || selectedPayout.Total_Regular_Hours || 0).toFixed(1)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-purple text-white">
                      <div className="card-body p-3">
                        <div className="small">Overtime Hours</div>
                        <div className="fs-4 fw-bold">{(selectedPayout.total_Overtime_Hours || selectedPayout.Total_Overtime_Hours || 0).toFixed(1)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-info text-white">
                      <div className="card-body p-3">
                        <div className="small">Net Amount</div>
                        <div className="fs-4 fw-bold">{formatCurrency(selectedPayout.net_Amount || selectedPayout.Net_Amount || 0)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Earnings Breakdown */}
                <div className="card mb-3">
                  <div className="card-header fw-bold">Earnings Breakdown</div>
                  <div className="card-body">
                    <table className="table table-sm mb-0">
                      <tbody>
                        <tr>
                          <td>Regular Earnings:</td>
                          <td className="text-end">{formatCurrency(selectedPayout.regular_Earnings || selectedPayout.Regular_Earnings || 0)}</td>
                        </tr>
                        <tr>
                          <td>Overtime Earnings:</td>
                          <td className="text-end">{formatCurrency(selectedPayout.overtime_Earnings || selectedPayout.Overtime_Earnings || 0)}</td>
                        </tr>
                        <tr>
                          <td>Paid Break Earnings:</td>
                          <td className="text-end">{formatCurrency(selectedPayout.paid_Break_Earnings || selectedPayout.Paid_Break_Earnings || 0)}</td>
                        </tr>
                        {((selectedPayout.total_Leave_Hours || selectedPayout.Total_Leave_Hours || 0) > 0 || 
                          (selectedPayout.total_Leave_Days || selectedPayout.Total_Leave_Days || 0) > 0 ||
                          (selectedPayout.leave_Adjustment_Amount || selectedPayout.Leave_Adjustment_Amount || 0) !== 0) && (
                          <>
                            <tr className="border-top">
                              <td><strong>Leave Information:</strong></td>
                              <td className="text-end"></td>
                            </tr>
                            {(selectedPayout.total_Leave_Hours || selectedPayout.Total_Leave_Hours || 0) > 0 && (
                              <tr>
                                <td className="ps-4">Leave Hours:</td>
                                <td className="text-end">{(selectedPayout.total_Leave_Hours || selectedPayout.Total_Leave_Hours || 0).toFixed(1)} hrs</td>
                              </tr>
                            )}
                            {(selectedPayout.total_Leave_Days || selectedPayout.Total_Leave_Days || 0) > 0 && (
                              <tr>
                                <td className="ps-4">Leave Days:</td>
                                <td className="text-end">{(selectedPayout.total_Leave_Days || selectedPayout.Total_Leave_Days || 0).toFixed(1)} days</td>
                              </tr>
                            )}
                            {(selectedPayout.leave_Earnings || selectedPayout.Leave_Earnings || 0) > 0 && (
                              <tr>
                                <td className="ps-4">Leave Earnings (Paid Leaves):</td>
                                <td className="text-end text-success">+{formatCurrency(selectedPayout.leave_Earnings || selectedPayout.Leave_Earnings || 0)}</td>
                              </tr>
                            )}
                            {(selectedPayout.leave_Adjustment_Amount || selectedPayout.Leave_Adjustment_Amount || 0) !== 0 && (
                              <tr>
                                <td className="ps-4">
                                  {(selectedPayout.leave_Adjustment_Amount || selectedPayout.Leave_Adjustment_Amount || 0) > 0 
                                    ? 'Leave Adjustment (Paid):' 
                                    : 'Leave Adjustment (Unpaid Deduction):'}
                                </td>
                                <td className={`text-end ${(selectedPayout.leave_Adjustment_Amount || selectedPayout.Leave_Adjustment_Amount || 0) > 0 ? 'text-success' : 'text-danger'}`}>
                                  {(selectedPayout.leave_Adjustment_Amount || selectedPayout.Leave_Adjustment_Amount || 0) > 0 ? '+' : ''}
                                  {formatCurrency(Math.abs(selectedPayout.leave_Adjustment_Amount || selectedPayout.Leave_Adjustment_Amount || 0))}
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                        <tr>
                          <td>Bonus:</td>
                          <td className="text-end text-success">+{formatCurrency(selectedPayout.bonus_Amount || selectedPayout.Bonus_Amount || 0)}</td>
                        </tr>
                        <tr>
                          <td>Deductions:</td>
                          <td className="text-end text-danger">-{formatCurrency(selectedPayout.deduction_Amount || selectedPayout.Deduction_Amount || 0)}</td>
                        </tr>
                        <tr className="fw-bold">
                          <td>Gross Amount:</td>
                          <td className="text-end">{formatCurrency(selectedPayout.gross_Amount || selectedPayout.Gross_Amount || 0)}</td>
                        </tr>
                        <tr>
                          <td>Tax:</td>
                          <td className="text-end text-danger">-{formatCurrency(selectedPayout.tax_Amount || selectedPayout.Tax_Amount || 0)}</td>
                        </tr>
                        <tr className="fw-bold border-top">
                          <td className="fs-5">Net Amount:</td>
                          <td className="text-end text-primary fs-5">{formatCurrency(selectedPayout.net_Amount || selectedPayout.Net_Amount || 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              {/* Leaves included in payout */}
              {selectedPayoutLeaves.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header fw-bold d-flex justify-content-between align-items-center">
                    <span>Leaves Included In This Payout</span>
                    <span className="text-muted small">
                      {selectedPayoutLeaves.length} record{selectedPayoutLeaves.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Leave</th>
                          <th>Dates</th>
                          <th className="text-center">Hours</th>
                          <th className="text-center">Days</th>
                          <th className="text-center">Status</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPayoutLeaves.map((leave) => {
                          const key = leave.sl_ID || leave.SL_ID;
                          const startDate = leave.startDate || leave.StartDate;
                          const endDate = leave.endDate || leave.EndDate || startDate;
                          const leaveDaysValue = Number(leave.leaveDays ?? leave.LeaveDays ?? (startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') + 1 : 0)) || 0;
                          const hoursValue = Number(leave.totalHours ?? leave.TotalHours ?? 0) || 0;
                          const isPaidLeave = (leave.isPaid ?? leave.IsPaid ?? false);
                          const amountValue = Number(leave.leaveAmount ?? leave.LeaveAmount ?? 0) || 0;
                          const reason = leave.reason || leave.Reason || '';
                          const leaveLabel = leave.leaveType || leave.LeaveType || 'Leave';

                          return (
                            <tr key={key}>
                              <td>
                                <div className="fw-semibold">{leaveLabel}</div>
                                {reason && <div className="text-muted small">{reason}</div>}
                              </td>
                              <td>
                                <div>
                                  {startDate ? moment(startDate).format('MMM DD, YYYY') : 'N/A'} -{' '}
                                  {endDate ? moment(endDate).format('MMM DD, YYYY') : 'N/A'}
                                </div>
                              </td>
                              <td className="text-center">{hoursValue.toFixed(1)} hrs</td>
                              <td className="text-center">{leaveDaysValue.toFixed(1)} days</td>
                              <td className="text-center">
                                <span className={`badge ${isPaidLeave ? 'bg-success' : 'bg-secondary'}`}>
                                  {isPaidLeave ? 'Paid' : 'Unpaid'}
                                </span>
                              </td>
                              <td className={`text-end ${amountValue >= 0 ? 'text-success' : 'text-danger'}`}>
                                {amountValue === 0
                                  ? formatCurrency(0)
                                  : `${amountValue > 0 ? '+' : '-'}${formatCurrency(Math.abs(amountValue))}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

                {/* Additional Information */}
                {(selectedPayout.notes || selectedPayout.Notes) && (
                  <div className="card">
                    <div className="card-header fw-bold">Notes</div>
                    <div className="card-body">
                      {selectedPayout.notes || selectedPayout.Notes}
                    </div>
                  </div>
                )}

                {/* Payment Information (if paid) */}
                {(selectedPayout.payout_Status || selectedPayout.Payout_Status) === 'PAID' && (
                  <div className="card mt-3">
                    <div className="card-header fw-bold">Payment Information</div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Payment Method:</strong> {selectedPayout.payment_Method || selectedPayout.Payment_Method || 'N/A'}
                        </div>
                        <div className="col-md-6">
                          <strong>Payment Reference:</strong> {selectedPayout.payment_Reference || selectedPayout.Payment_Reference || 'N/A'}
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>Paid By:</strong> {selectedPayout.paidBy || selectedPayout.PaidBy || 'N/A'}
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>Paid Date:</strong> {selectedPayout.paidDateTime || selectedPayout.PaidDateTime ? moment(selectedPayout.paidDateTime || selectedPayout.PaidDateTime).format('MMM DD, YYYY HH:mm') : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderColor: isDarkMode ? '#404040' : '#dee2e6' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClosePayoutDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Process Payment Modal */}
      {showProcessModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content" style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000'
            }}>
              <div className="modal-header" style={{
                borderColor: isDarkMode ? '#404040' : '#dee2e6',
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000'
              }}>
                <h5 className="modal-title">Process Payment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowProcessModal(false)}
                  style={{ filter: isDarkMode ? 'invert(1)' : 'none' }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Payment Method <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                    style={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      color: isDarkMode ? '#ffffff' : '#000000',
                      borderColor: isDarkMode ? '#374151' : '#dee2e6'
                    }}
                  >
                    <option value="">-- Select Payment Method --</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CASH">Cash</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="PAYPAL">PayPal</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Payment Reference / Transaction ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={paymentForm.paymentReference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentReference: e.target.value })}
                    placeholder="Enter reference number"
                    style={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      color: isDarkMode ? '#ffffff' : '#000000',
                      borderColor: isDarkMode ? '#374151' : '#dee2e6'
                    }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    value={paymentForm.paymentNotes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentNotes: e.target.value })}
                    rows="3"
                    placeholder="Enter any notes..."
                    style={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      color: isDarkMode ? '#ffffff' : '#000000',
                      borderColor: isDarkMode ? '#374151' : '#dee2e6'
                    }}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer" style={{ borderColor: isDarkMode ? '#404040' : '#dee2e6' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowProcessModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmProcessPayout}
                  disabled={!paymentForm.paymentMethod}
                >
                  <i className="fa fa-check me-2"></i>
                  Process Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPayout;

